/**
 * Maps a selected MapLibre feature to a report draft: lat/lon from the geometry
 * (via $lib/featureGeometry) plus a snapshot of feature context into
 * location_info. Pure (no stores/fetch) so it's unit testable. Returns null
 * when the feature has no usable coordinate.
 */

import type { MapGeoJSONFeature } from 'maplibre-gl';
import {
	parseGeometryLocation,
	parseValidPosition,
	formatPointGeometry
} from '#lib/featureGeometry.js';
import type { ReportContext, ReportLocationInfo } from './issueReporterClient';

/** Round a coordinate to 6 decimal places (~0.11 m at the equator). */
const round6 = (n: number): number => Math.round(n * 1e6) / 1e6;

export interface ReportDraft {
	lat: number;
	lon: number;
	source: string;
	positionLabel: string;
	locationInfo: ReportLocationInfo;
	/**
	 * Selected feature's GeoJSON geometry, verbatim at FULL precision —
	 * deliberately NOT rounded like lat/lon, so the feature can be recreated
	 * exactly and diffed against the live dataset to detect regressions. Stored
	 * as its own DB column, kept out of `locationInfo`.
	 */
	geometry: MapGeoJSONFeature['geometry'];
}

export function buildReportDraft(
	feature: MapGeoJSONFeature,
	source: string,
	systemId: string | undefined,
	context?: ReportContext
): ReportDraft | null {
	const position = parseValidPosition(parseGeometryLocation(feature.geometry));
	if (!position) return null;

	const [lon, lat] = position;
	const positionLabel = formatPointGeometry(position);
	const featureId = feature.id ?? (feature.properties?.id as string | number | undefined) ?? null;

	return {
		// 6 d.p. (~0.11 m) — ample for a data-issue location, avoids persisting
		// full float precision. positionLabel stays at the helper's 5 d.p.
		lat: round6(lat),
		lon: round6(lon),
		source,
		positionLabel,
		geometry: feature.geometry,
		locationInfo: {
			app: source,
			systemId: systemId ?? null,
			layerId: feature.layer?.id ?? null,
			featureId,
			position: positionLabel,
			properties: { ...feature.properties },
			...(context ? { context } : {})
		}
	};
}
